import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { toast } from 'sonner';

export interface EtsyListingTemplate {
  title_template: string;
  description_template: string;
  price: number;
  quantity: number;
  taxonomy_id: number;
  who_made: 'i_did' | 'someone_else' | 'collective';
  when_made: string;
  is_supply: boolean;
  tags?: string[];
}

export interface AutomationJob {
  id: string;
  job_name: string;
  base_prompt: string;
  prompt_variations: any;
  models_to_use: any;
  total_images_target: number;
  images_generated: number;
  size: string;
  quality: string;
  style: string;
  n: number;
  status: string;
  start_time: string | null;
  end_time: string | null;
  pause_time: string | null;
  rpm_limit: number;
  requests_made_this_minute: number;
  last_request_time: string | null;
  success_count: number;
  error_count: number;
  last_error: string | null;
  retry_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  selected_api_key_id: string | null;
  downloaded_at: string | null;
  // New fields for Etsy/Drive automation
  google_drive_enabled: boolean;
  etsy_auto_list: boolean;
  etsy_listing_template: EtsyListingTemplate | any;
}

export const useAutomationJobs = () => {
  const { user } = useSecureAuthContext();
  const [jobs, setJobs] = useState<AutomationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const rateLimitUntilRef = useRef<Map<string, number>>(new Map());

  const fetchJobs = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load automation jobs');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJobs();

    // Set up realtime subscription
    const channel = supabase
      .channel('automation-jobs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automated_generation_jobs'
        },
        (payload) => {
          console.log('Job update:', payload);
          fetchJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchJobs]);

  const createJob = async (jobData: {
    job_name: string;
    base_prompt: string;
    prompt_variations?: string[];
    models_to_use: string[];
    total_images_target: number;
    size?: string;
    quality?: string;
    style?: string;
    n?: number;
    rpm_limit?: number;
    selected_api_key_id?: string | null;
    // New fields for Etsy/Drive automation
    google_drive_enabled?: boolean;
    etsy_auto_list?: boolean;
    etsy_listing_template?: EtsyListingTemplate | null;
  }, showToast: boolean = true) => {
    if (!user?.id) {
      if (showToast) toast.error('User not authenticated');
      return null;
    }

    try {
      const insertData = {
        job_name: jobData.job_name,
        base_prompt: jobData.base_prompt,
        prompt_variations: jobData.prompt_variations,
        models_to_use: jobData.models_to_use,
        total_images_target: jobData.total_images_target,
        size: jobData.size,
        quality: jobData.quality,
        style: jobData.style,
        n: jobData.n,
        rpm_limit: jobData.rpm_limit,
        selected_api_key_id: jobData.selected_api_key_id,
        google_drive_enabled: jobData.google_drive_enabled || false,
        etsy_auto_list: jobData.etsy_auto_list || false,
        etsy_listing_template: jobData.etsy_listing_template as any,
        created_by: user.id,
        status: 'pending'
      };
      
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      if (showToast) toast.success('Automation job created successfully');
      fetchJobs();
      return data;
    } catch (error) {
      console.error('Error creating job:', error);
      if (showToast) toast.error('Failed to create automation job');
      return null;
    }
  };

  // Create multiple jobs from bulk prompts
  const createBulkJobs = async (
    prompts: { number: number; title: string; prompt: string }[],
    settings: {
      models_to_use: string[];
      size: string;
      quality: string;
      style: string;
      images_per_prompt: number;
    }
  ) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return [];
    }

    const createdJobs: AutomationJob[] = [];
    const toastId = toast.loading(`Creating ${prompts.length} jobs...`);

    try {
      for (let i = 0; i < prompts.length; i++) {
        const prompt = prompts[i];
        const jobName = prompt.title; // Use title only, no number prefix
        
        const job = await createJob({
          job_name: jobName,
          base_prompt: prompt.prompt,
          models_to_use: settings.models_to_use,
          total_images_target: settings.images_per_prompt,
          size: settings.size,
          quality: settings.quality,
          style: settings.style,
          rpm_limit: 5
        }, false); // Don't show individual toasts

        if (job) {
          createdJobs.push(job);
        }
        
        // Update progress
        if ((i + 1) % 5 === 0 || i === prompts.length - 1) {
          toast.loading(`Created ${i + 1}/${prompts.length} jobs...`, { id: toastId });
        }
      }

      toast.success(`Created ${createdJobs.length} automation jobs`, { id: toastId });
      await fetchJobs();
      return createdJobs;
    } catch (error) {
      console.error('Error creating bulk jobs:', error);
      toast.error('Failed to create some jobs', { id: toastId });
      return createdJobs;
    }
  };

  // Start all pending jobs
  const startAllPending = async () => {
    const pendingJobs = jobs.filter(j => j.status === 'pending');
    if (pendingJobs.length === 0) {
      toast.error('No pending jobs to start');
      return;
    }

    const toastId = toast.loading(`Starting ${pendingJobs.length} jobs...`);
    
    try {
      for (const job of pendingJobs) {
        await supabase
          .from('automated_generation_jobs')
          .update({
            status: 'running',
            start_time: new Date().toISOString(),
            pause_time: null
          })
          .eq('id', job.id);
      }

      // Trigger processing
      await supabase.functions.invoke('process-automation-job', { body: {} });
      
      toast.success(`Started ${pendingJobs.length} jobs`, { id: toastId });
      await fetchJobs();
    } catch (error) {
      console.error('Error starting jobs:', error);
      toast.error('Failed to start some jobs', { id: toastId });
    }
  };

  // Start all paused jobs
  const startAllPaused = async () => {
    const pausedJobs = jobs.filter(j => j.status === 'paused');
    if (pausedJobs.length === 0) {
      toast.error('No paused jobs to start');
      return;
    }

    const toastId = toast.loading(`Starting ${pausedJobs.length} paused jobs...`);
    
    try {
      for (const job of pausedJobs) {
        await supabase
          .from('automated_generation_jobs')
          .update({
            status: 'running',
            start_time: new Date().toISOString(),
            pause_time: null
          })
          .eq('id', job.id);
      }

      // Trigger processing
      await supabase.functions.invoke('process-automation-job', { body: {} });
      
      toast.success(`Started ${pausedJobs.length} paused jobs`, { id: toastId });
      await fetchJobs();
    } catch (error) {
      console.error('Error starting paused jobs:', error);
      toast.error('Failed to start some jobs', { id: toastId });
    }
  };

  // Pause all running jobs
  const pauseAllRunning = async () => {
    const runningJobs = jobs.filter(j => j.status === 'running');
    if (runningJobs.length === 0) {
      toast.error('No running jobs to pause');
      return;
    }

    const toastId = toast.loading(`Pausing ${runningJobs.length} jobs...`);
    
    try {
      for (const job of runningJobs) {
        await supabase
          .from('automated_generation_jobs')
          .update({
            status: 'paused',
            pause_time: new Date().toISOString()
          })
          .eq('id', job.id);
      }

      toast.success(`Paused ${runningJobs.length} jobs`, { id: toastId });
      await fetchJobs();
    } catch (error) {
      console.error('Error pausing jobs:', error);
      toast.error('Failed to pause some jobs', { id: toastId });
    }
  };

  const startJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'running',
          start_time: new Date().toISOString(),
          pause_time: null // Clear pause time when resuming
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job started');
      
      // Immediately trigger processing by calling the edge function
      try {
        await supabase.functions.invoke('process-automation-job', {
          body: { jobId }
        });
        console.log('✅ Triggered immediate processing for job:', jobId);
      } catch (invokeError) {
        console.log('⚠️ Could not immediately trigger processing:', invokeError);
      }
      
      fetchJobs();
    } catch (error) {
      console.error('Error starting job:', error);
      toast.error('Failed to start job');
    }
  };


  const pauseJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'paused',
          pause_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job paused');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error pausing job:', error);
      toast.error('Failed to pause job');
    }
  };

  const stopJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'stopped',
          end_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job stopped');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error('Failed to stop job');
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job deleted');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const markJobsAsDownloaded = async (jobIds: string[]) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({ downloaded_at: new Date().toISOString() })
        .in('id', jobIds);

      if (error) throw error;
      
      await fetchJobs(); // Refresh to show updated status
    } catch (error) {
      console.error('Error marking jobs as downloaded:', error);
      throw error;
    }
  };

  const updateJob = async (
    jobId: string,
    updates: { 
      total_images_target?: number; 
      job_name?: string 
    }
  ): Promise<void> => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;
      toast.success('Job updated successfully');
      await fetchJobs();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job');
      throw error;
    }
  };

  const mergeJobs = async (
    jobIds: string[], 
    newJobName: string
  ): Promise<AutomationJob | null> => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return null;
    }

    if (jobIds.length < 2) {
      toast.error('Select at least 2 jobs to merge');
      return null;
    }

    const toastId = toast.loading('Merging jobs...');

    try {
      // 1. Fetch all jobs to merge
      const jobsToMerge = jobs.filter(j => jobIds.includes(j.id));
      
      // 2. Check for running jobs
      const runningJobs = jobsToMerge.filter(j => j.status === 'running');
      if (runningJobs.length > 0) {
        toast.error('Cannot merge running jobs. Pause them first.', { id: toastId });
        return null;
      }

      // 3. Calculate combined totals
      const totalTarget = jobsToMerge.reduce((sum, j) => sum + j.total_images_target, 0);
      const totalGenerated = jobsToMerge.reduce((sum, j) => sum + (j.success_count || j.images_generated), 0);
      const totalSuccessCount = jobsToMerge.reduce((sum, j) => sum + (j.success_count || 0), 0);
      const totalErrorCount = jobsToMerge.reduce((sum, j) => sum + (j.error_count || 0), 0);

      // 4. Use first job's settings as template
      const templateJob = jobsToMerge[0];

      // 5. Create new merged job
      const { data: newJob, error: createError } = await supabase
        .from('automated_generation_jobs')
        .insert({
          job_name: newJobName,
          base_prompt: templateJob.base_prompt,
          prompt_variations: templateJob.prompt_variations,
          models_to_use: templateJob.models_to_use,
          total_images_target: totalTarget,
          images_generated: totalGenerated,
          success_count: totalSuccessCount,
          error_count: totalErrorCount,
          size: templateJob.size,
          quality: templateJob.quality,
          style: templateJob.style,
          n: templateJob.n,
          rpm_limit: templateJob.rpm_limit,
          status: totalGenerated >= totalTarget ? 'completed' : 'pending',
          created_by: user.id,
          google_drive_enabled: templateJob.google_drive_enabled,
          etsy_auto_list: templateJob.etsy_auto_list,
          etsy_listing_template: templateJob.etsy_listing_template as any
        })
        .select()
        .single();

      if (createError) throw createError;

      // 6. Reassign all images to new merged job
      const { error: updateImagesError } = await supabase
        .from('generated_images')
        .update({ automated_job_id: newJob.id })
        .in('automated_job_id', jobIds);

      if (updateImagesError) throw updateImagesError;

      // 7. Delete source jobs
      const { error: deleteError } = await supabase
        .from('automated_generation_jobs')
        .delete()
        .in('id', jobIds);

      if (deleteError) throw deleteError;

      toast.success(`Merged ${jobIds.length} jobs into "${newJobName}"`, { id: toastId });
      await fetchJobs();
      return newJob;
    } catch (error) {
      console.error('Error merging jobs:', error);
      toast.error('Failed to merge jobs', { id: toastId });
      return null;
    }
  };

  return {
    jobs,
    loading,
    processing,
    createJob,
    createBulkJobs,
    startJob,
    startAllPending,
    startAllPaused,
    pauseJob,
    pauseAllRunning,
    stopJob,
    deleteJob,
    markJobsAsDownloaded,
    updateJob,
    mergeJobs,
    refreshJobs: fetchJobs
  };
};
